import React, { FC, useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import JwtValidator from '../../utils/JwtValidator';
import { AccountSolution } from '@thingslog/repositories';
import { useViewport } from '../../hooks';
import { Typography } from '@mui/material';

interface PageFooterProps {
  className?: string;
  mainLabel?: string;
  link?: {
    label?: string;
    url: string;
  };
}

const getSolutionTranslationKey = (solution: AccountSolution): string => {
  const solutionMap: Record<AccountSolution, string> = {
    [AccountSolution.NONE]: 'solution_none',
    [AccountSolution.AGRI]: 'solution_agri',
    [AccountSolution.STREET_LIGHTS]: 'solution_street_lights',
    [AccountSolution.AIR_QUALITY]: 'solution_air_quality',
    [AccountSolution.PUMP_STATION]: 'solution_pump_station',
    [AccountSolution.SMART_WASTE]: 'solution_smart_waste',
    [AccountSolution.LEVEL]: 'solution_level',
    [AccountSolution.GAS]: 'solution_gas',
    [AccountSolution.PRESSURE]: 'solution_pressure',
    [AccountSolution.SMART_WATER]: 'solution_smart_water',
    [AccountSolution.ENERGY]: 'solution_energy',
    [AccountSolution.ENVIRONMENT_MONITORING]: 'solution_environment_monitoring',
  };
  return solutionMap[solution] || 'solution_none';
};

export const PageFooter: FC<PageFooterProps> = ({
  mainLabel,
  link,
  className,
}: PageFooterProps) => {
  const { t } = useTranslation();
  const jwtValidator = useMemo(() => new JwtValidator(), []);
  const solution = jwtValidator.decodedToken?.solution;
  const showSolution = solution && solution !== AccountSolution.NONE;
  const solutionLink = solution ? window.runConfig.solutionLinks?.[solution] : undefined;
  const isMobile = useViewport('sm');

  const mainLabelText = useMemo(() => {
    return mainLabel !== undefined ? mainLabel : t('powered_by');
  }, [mainLabel, t]);

  const shouldOverwriteLink = useMemo(() => {
    return mainLabel === undefined;
  }, [mainLabel]);

  const linkText = useMemo(() => {
    if (shouldOverwriteLink) {
      return { label: 'ThingsLog', url: 'https://thingslog.com/' };
    } else if (link !== undefined && link.url !== undefined) {
      return { label: link.label || link.url, url: link.url };
    } else {
      return null;
    }
  }, [link, shouldOverwriteLink, t]);

  const solutionText = useMemo(() => {
    if (showSolution && solution) {
      return { label: t(getSolutionTranslationKey(solution)), url: solutionLink };
    } else {
      return null;
    }
  }, [showSolution, solution, t]);

  return (
    <section
      className={clsx('flex min-h-[1.5rem] items-center justify-center gap-1 px-2', className)}
    >
      <Typography variant={isMobile ? 'caption' : 'body2'}>{mainLabelText}</Typography>
      {linkText && (
        <Typography variant={isMobile ? 'caption' : 'body2'}>
          <a href={linkText.url} target="_blank" rel="noreferrer">
            {linkText.label}
          </a>
        </Typography>
      )}
      {solutionText && <Typography variant={isMobile ? 'caption' : 'body2'}>•</Typography>}
      {solutionText && (
        <Typography variant={isMobile ? 'caption' : 'body2'}>
          <a target="_blank" rel="noreferrer" href={solutionText.url}>
            {solutionText.label}
          </a>
        </Typography>
      )}
    </section>
  );
};
